/*
 * Product add-ons (replaces Globo Product Options). Markup: snippets/product-addons.liquid.
 *
 * - Intercepts the product form submit when the form has add-on groups and sends one /cart/add.js call: the main item
 *   (text choices as visible properties, hidden _addon_group) plus one line per chosen add-on variant (visible "For",
 *   hidden _addon_parent). Then fires the same variant:add / cart:change events as the theme's ProductForm, so the
 *   cart drawer and cart count update as usual.
 * - Keeps add-on lines in step with their parent in the cart: removing the parent removes its add-ons, and add-on
 *   quantities follow the parent quantity (or stay at 1 for "Always 1" add-ons).
 * - Shows the running total (variant price + chosen paid add-ons) in the product price block and the sticky bar.
 */
(() => {
  if (window.tenetProductAddons) return;
  window.tenetProductAddons = true;

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const groupsForForm = (formId) => Array.from(document.querySelectorAll(`[data-addon-form="${CSS.escape(formId)}"]`));

  // Returns null when nothing is chosen, otherwise { value, label } (value is '' for the "None" choice)
  const selectionOf = (group) => {
    const select = group.querySelector('select');

    if (select) {
      const option = select.selectedOptions[0];
      if (!option || option.hasAttribute('data-addon-placeholder')) return null;
      return { value: option.value, label: option.dataset.choiceLabel || option.textContent.trim() };
    }

    const checked = group.querySelector('input:checked');
    if (!checked) return null;
    return { value: checked.value, label: checked.dataset.choiceLabel || checked.value };
  };

  const updateGroup = (group) => {
    const selection = selectionOf(group);
    const selectedLabel = group.querySelector('[data-addon-selected-label]');

    if (selectedLabel && !group.querySelector('select') && group.querySelector('input[type="radio"]')) {
      selectedLabel.textContent = selection ? selection.label : '';
    }

    if (selection) {
      group.querySelector('.product-addon__error')?.setAttribute('hidden', '');
    }
  };

  // Running total: variant unit price + each chosen paid add-on (one per main item; included parts count as $0)
  const basePrices = {};

  const formatMoney = (cents) => {
    const settings = window.themeVariables?.settings || {};
    const format = (settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat) || '${{amount}}';
    const withDelimiters = (decimals, thousands, decimal) => {
      const [whole, fraction] = (cents / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => ({
      amount: withDelimiters(2, ',', '.'),
      amount_no_decimals: withDelimiters(0, ',', '.'),
      amount_with_comma_separator: withDelimiters(2, '.', ','),
      amount_no_decimals_with_comma_separator: withDelimiters(0, '.', ','),
      amount_with_apostrophe_separator: withDelimiters(2, "'", '.')
    })[key] ?? withDelimiters(2, ',', '.'));
  };

  // Short form for the note under the price, e.g. "+$5" or "+$12.50"
  const shortMoney = (cents) => `+$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

  // Shared with the wheelset configurator (snippets/wheelset-configurator-standalone.liquid)
  window.tenetAddonPrice = { formatMoney, shortMoney };

  const addonTotal = (groups) => groups.reduce((sum, group) => {
    const select = group.querySelector('select');
    const chosen = select ? select.selectedOptions[0] : group.querySelector('input:checked');
    return sum + (Number(chosen?.dataset.choicePrice) || 0);
  }, 0);

  const setPrice = (element, cents) => {
    if (!element) return;
    if (element.addonOriginalHtml === undefined) element.addonOriginalHtml = element.innerHTML;

    if (cents === null) {
      element.innerHTML = element.addonOriginalHtml;
      return;
    }

    // The store's money format includes markup (<span class=money>), same as the theme's Liquid output
    const srOnly = element.querySelector('.sr-only');
    element.innerHTML = formatMoney(cents);
    if (srOnly) element.prepend(srOnly);
  };

  const updateTotals = (formId) => {
    const groups = groupsForForm(formId);
    if (groups.length === 0) return;

    const base = basePrices[formId] || {
      price: Number(groups[0].dataset.addonBasePrice) || 0,
      compare: Number(groups[0].dataset.addonBaseCompare) || 0
    };
    const extra = addonTotal(groups);
    const section = groups[0].closest('.shopify-section') || document;
    const priceLists = [
      ...section.querySelectorAll('[data-block-type="price"] price-list'),
      ...section.querySelectorAll(`product-sticky-bar[form="${CSS.escape(formId)}"] price-list`)
    ];

    // With no paid add-on, put back the theme's own markup; after a variant change write the new variant's price
    // instead, since the cached markup may belong to the previous variant
    const variantChanged = Boolean(basePrices[formId]);

    priceLists.forEach((priceList) => {
      setPrice(priceList.querySelector('sale-price'), extra > 0 || variantChanged ? base.price + extra : null);
      setPrice(priceList.querySelector('compare-at-price'), (extra > 0 || variantChanged) && base.compare > base.price ? base.compare + extra : null);
    });

    const priceBlock = section.querySelector('[data-block-type="price"]');
    if (priceBlock) {
      let note = priceBlock.querySelector('.product-addon__total-note');
      if (extra > 0) {
        if (!note) {
          note = document.createElement('p');
          note.className = 'product-addon__total-note text-sm text-subdued';
          priceBlock.querySelector('price-list')?.after(note);
        }
        note.textContent = `Includes add-ons: ${shortMoney(extra)}`;
      } else {
        note?.remove();
      }
    }
  };

  const formIdOf = (group) => group.dataset.addonForm;

  document.addEventListener('change', (event) => {
    const group = event.target.closest?.('[data-addon-form]');
    if (!group) return;
    updateGroup(group);
    updateTotals(formIdOf(group));
  });

  // The theme re-renders the price block (not the add-on blocks) on variant change, then fires variant:change
  document.addEventListener('variant:change', (event) => {
    const formId = event.target?.getAttribute?.('id');
    const variant = event.detail?.variant;
    if (!formId || groupsForForm(formId).length === 0) return;

    if (variant) {
      basePrices[formId] = { price: Number(variant.price) || 0, compare: Number(variant.compare_at_price) || 0 };
    }

    // Let the theme finish swapping the price block and updating the sticky bar first
    requestAnimationFrame(() => setTimeout(() => updateTotals(formId), 0));
  });

  const initGroups = () => document.querySelectorAll('[data-addon-form]').forEach(updateGroup);

  const initTotals = () => new Set(Array.from(document.querySelectorAll('[data-addon-form]'), formIdOf)).forEach(updateTotals);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initGroups(); initTotals(); }, { once: true });
  } else {
    initGroups();
    initTotals();
  }

  // Re-run when the product info is re-rendered (variant change)
  let initQueued = false;
  new MutationObserver(() => {
    if (initQueued) return;
    initQueued = true;
    requestAnimationFrame(() => { initQueued = false; initGroups(); });
  }).observe(document.body || document.documentElement, { childList: true, subtree: true });

  const newGroupId = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

  const addToCart = async (form, groups, submitter) => {
    let firstInvalid = null;

    groups.forEach((group) => {
      if (group.hasAttribute('data-addon-required') && !selectionOf(group)) {
        group.querySelector('.product-addon__error')?.removeAttribute('hidden');
        firstInvalid = firstInvalid || group;
      }
    });

    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const quantity = Math.max(1, parseInt(formData.get('quantity') || '1', 10));
    const properties = {};

    formData.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== '') properties[match[1]] = value;
    });

    const groupId = newGroupId();
    const addonItems = [];

    groups.forEach((group) => {
      const selection = selectionOf(group);
      if (!selection || selection.value === '') return;

      if (group.dataset.addonMode === 'text') {
        properties[group.dataset.addonLabel] = selection.value;
      } else {
        const single = group.dataset.addonQuantity === 'single';
        const addonProperties = { For: group.dataset.addonParentTitle, _addon_parent: groupId };
        if (single) addonProperties._addon_quantity = 'single';

        addonItems.push({ id: Number(selection.value), quantity: single ? 1 : quantity, properties: addonProperties });
      }
    });

    if (addonItems.length > 0) properties._addon_group = groupId;

    const mainItem = { id: Number(formData.get('id')), quantity, properties };
    if (formData.get('selling_plan')) mainItem.selling_plan = formData.get('selling_plan');

    const submitButtons = Array.from(form.elements).filter((element) => element.type === 'submit');
    if (submitter?.getAttribute('aria-busy') === 'true') return;
    submitButtons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [mainItem, ...addonItems],
        sections: sectionsToBundle.join(','),
        sections_url: `${root()}variants/${mainItem.id}`
      })
    });

    submitButtons.forEach((button) => button.removeAttribute('aria-busy'));
    const responseJson = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));

    if (!response.ok) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson.description } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      return;
    }

    const onSuccessDo = formData.get('on_success');

    if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
      window.location.href = `${root()}cart`;
      return;
    }

    const cartContent = await (await fetch(`${root()}cart.js`)).json();
    cartContent.sections = responseJson.sections;

    form.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: { items: responseJson.items || [responseJson], cart: cartContent, onSuccessDo }
    }));

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', onSuccessDo, cart: cartContent }
    }));
  };

  // Capture phase on document runs before the theme's <product-form> submit listener
  document.addEventListener('submit', (event) => {
    const form = event.target;
    // Product forms have an input named "id", which shadows form.id
    const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
    if (!formId) return;

    const groups = groupsForForm(formId);
    if (groups.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addToCart(form, groups, event.submitter);
  }, true);

  // Cart: add-on lines follow their parent line
  let reconciling = false;

  const reconcileCart = async (cart) => {
    if (reconciling || !cart?.items) return false;

    const parentQuantities = {};
    cart.items.forEach((item) => {
      const group = item.properties?._addon_group;
      if (group) parentQuantities[group] = (parentQuantities[group] || 0) + item.quantity;
    });

    const updates = {};
    cart.items.forEach((item) => {
      const parent = item.properties?._addon_parent;
      if (!parent) return;

      const parentQuantity = parentQuantities[parent] || 0;
      const targetQuantity = item.properties._addon_quantity === 'single' ? Math.min(parentQuantity, 1) : parentQuantity;
      if (item.quantity !== targetQuantity) updates[item.key] = targetQuantity;
    });

    if (Object.keys(updates).length === 0) return false;

    reconciling = true;
    try {
      await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
    } finally {
      reconciling = false;
    }

    return true;
  };

  document.addEventListener('cart:change', async (event) => {
    if (event.detail?.baseEvent !== 'line-item:change') return;
    if (await reconcileCart(event.detail.cart)) document.dispatchEvent(new CustomEvent('cart:refresh'));
  });

  // The cart page reloads after every quantity change, so check once on load
  if (window.themeVariables?.settings?.pageType === 'cart' || window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
    fetch(`${root()}cart.js`)
      .then((response) => response.json())
      .then(reconcileCart)
      .then((changed) => { if (changed) window.location.reload(); })
      .catch(() => {});
  }
})();
